import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  InputAdornment,
  Box,
  Chip,
  Typography,
  Button,
  Tabs,
  Tab,
  Badge,
  Fab,
  Divider,
  Paper
} from '@mui/material';
import {
  Search,
  Add,
  Close,
  Restaurant,
  Coffee,
  Cake,
  LocalBar,
  Remove
} from '@mui/icons-material';
import { useStore } from '../store/useStore';
import { useLanguage } from '../contexts/LanguageContext';

interface MenuDialogProps {
  open: boolean;
  onClose: () => void;
  tableId: string;
}

interface CartItem {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  notes?: string;
}

const MenuDialog: React.FC<MenuDialogProps> = ({ open, onClose, tableId }) => {
  const { menuItems, addItemToOrder } = useStore();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cart, setCart] = useState<CartItem[]>([]);
  const isLandscape = window.matchMedia('(orientation: landscape)').matches;
  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
  const isTabletLandscape = isLandscape && isTablet;
  const isMobileLandscape = isLandscape && window.innerWidth < 768;
  const isAnyLandscape = isMobileLandscape || isTabletLandscape;

  const categories = useMemo(() => {
    const cats = ['All', ...Array.from(new Set(menuItems.map(item => item.category)))];
    return cats;
  }, [menuItems]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Starters': return <Restaurant />;
      case 'Mains': return <Restaurant />;
      case 'Desserts': return <Cake />;
      case 'Beverages': return <Coffee />;
      default: return <LocalBar />;
    }
  };

  const getCategoryLabel = (category: string) => {
    if (category === 'All') return t('common.all') || 'All';
    switch (category) {
      case 'Starters': return t('menu.starters');
      case 'Mains': return t('menu.mains');
      case 'Desserts': return t('menu.desserts');
      case 'Beverages': return t('menu.beverages');
      default: return category;
    }
  };

  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const itemName = item.translationKey ? t(`menu.items.${item.translationKey}`) : item.name;
      const itemDescription = item.descriptionKey ? t(`menu.items.${item.descriptionKey}`) : item.description;
      const matchesSearch = itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           itemDescription?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [menuItems, searchQuery, selectedCategory, t]);

  const handleAddToCart = (menuItem: typeof menuItems[0]) => {
    const existingItem = cart.find(item => item.menuItemId === menuItem.id);
    const itemName = menuItem.translationKey ? t(`menu.items.${menuItem.translationKey}`) : menuItem.name;
    if (existingItem) {
      setCart(cart.map(item =>
        item.menuItemId === menuItem.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        menuItemId: menuItem.id,
        name: itemName,
        price: menuItem.price,
        quantity: 1
      }]);
    }
  };

  const handleUpdateQuantity = (menuItemId: string, delta: number) => {
    setCart(cart.map(item => {
      if (item.menuItemId === menuItemId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean) as CartItem[]);
  };

  const handleConfirmOrder = () => {
    cart.forEach(item => {
      const menuItem = menuItems.find(m => m.id === item.menuItemId);
      if (menuItem) {
        addItemToOrder(tableId, menuItem, item.quantity, item.notes);
      }
    });
    setCart([]);
    onClose();
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isTabletLandscape ? "lg" : "md"} 
      fullWidth
      PaperProps={{
        sx: { 
          height: isAnyLandscape ? '95vh' : '90vh', 
          display: 'flex', 
          flexDirection: 'column',
          maxWidth: isTabletLandscape ? '1100px' : '900px'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#b5cbb3',
        color: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: isAnyLandscape ? 0.5 : 2,
        px: isAnyLandscape ? 1.5 : 3,
        minHeight: isAnyLandscape ? 40 : 'auto'
      }}>
        <Typography variant={isAnyLandscape ? 'body2' : 'h6'} sx={{ fontWeight: isAnyLandscape ? 600 : 400 }}>
          {t('navigation.menu')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#1a1a1a', p: isAnyLandscape ? 0.25 : 1 }}>
          <Close fontSize={isAnyLandscape ? 'small' : 'medium'} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Portrait/Mobile Layout */}
        {!isTabletLandscape && (
          <>
            <Box sx={{ p: isAnyLandscape ? 1 : 2, borderBottom: '1px solid #e0e0e0' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <Tabs
                value={selectedCategory}
                onChange={(_, value) => setSelectedCategory(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    color: '#757575',
                    '&.Mui-selected': {
                      color: '#424242',
                      backgroundColor: '#f0f0f0',
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#424242'
                  }
                }}
              >
                {categories.map((category) => (
                  <Tab
                    key={category}
                    label={getCategoryLabel(category)}
                    value={category}
                    icon={getCategoryIcon(category)}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <List>
                {filteredItems.map((item) => {
                  const cartItem = cart.find(c => c.menuItemId === item.id);
                  const quantity = cartItem?.quantity || 0;
                  
                  return (
                    <Paper
                      key={item.id}
                      sx={{ 
                        mb: 2, 
                        p: 2,
                        borderRadius: 2,
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <ListItem disablePadding>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {item.translationKey ? t(`menu.items.${item.translationKey}`) : item.name}
                              </Typography>
                              {!item.available && (
                                <Chip label={t('tableStatus.occupied')} size="small" color="error" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              {item.description && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  {item.descriptionKey ? t(`menu.items.${item.descriptionKey}`) : item.description}
                                </Typography>
                              )}
                              <Typography variant="body1" sx={{ mt: 1, color: '#424242', fontWeight: 700, fontSize: '1.2rem' }} component="span" display="block">
                                €{item.price.toFixed(2)}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          {quantity > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.id, -1)}
                              >
                                <Remove />
                              </IconButton>
                              <Typography sx={{ mx: 1, minWidth: 20, textAlign: 'center' }}>
                                {quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.id, 1)}
                                disabled={!item.available}
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              color="primary"
                              onClick={() => handleAddToCart(item)}
                              disabled={!item.available}
                            >
                              <Add />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    </Paper>
                  );
                })}
              </List>
            </Box>
          </>
        )}

        {/* Landscape Layout */}
        {isTabletLandscape && (
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '250px 1fr',
            flex: 1,
            overflow: 'hidden'
          }}>
            {/* Left sidebar */}
            <Box sx={{ 
              borderRight: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <Tabs
                value={selectedCategory}
                onChange={(_, value) => setSelectedCategory(value)}
                orientation="vertical"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    color: '#757575',
                    '&.Mui-selected': {
                      color: '#424242',
                      backgroundColor: '#f0f0f0',
                      fontWeight: 600
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#424242'
                  }
                }}
              >
                {categories.map((category) => (
                  <Tab
                    key={category}
                    label={getCategoryLabel(category)}
                    value={category}
                    icon={getCategoryIcon(category)}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>

            {/* Right content */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto', 
              p: 2
            }}>
              <List>
                {filteredItems.map((item) => {
                  const cartItem = cart.find(c => c.menuItemId === item.id);
                  const quantity = cartItem?.quantity || 0;
                  
                  return (
                    <Paper
                      key={item.id}
                      sx={{ 
                        mb: 2, 
                        p: 2,
                        borderRadius: 2,
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <ListItem disablePadding>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {item.translationKey ? t(`menu.items.${item.translationKey}`) : item.name}
                              </Typography>
                              {!item.available && (
                                <Chip label={t('tableStatus.occupied')} size="small" color="error" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              {item.description && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  {item.descriptionKey ? t(`menu.items.${item.descriptionKey}`) : item.description}
                                </Typography>
                              )}
                              <Typography variant="body1" sx={{ mt: 1, color: '#424242', fontWeight: 700, fontSize: '1.2rem' }} component="span" display="block">
                                €{item.price.toFixed(2)}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          {quantity > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.id, -1)}
                              >
                                <Remove />
                              </IconButton>
                              <Typography sx={{ mx: 1, minWidth: 20, textAlign: 'center' }}>
                                {quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.id, 1)}
                                disabled={!item.available}
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              color="primary"
                              onClick={() => handleAddToCart(item)}
                              disabled={!item.available}
                            >
                              <Add />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    </Paper>
                  );
                })}
              </List>
            </Box>
          </Box>
        )}

        {/* Cart Footer */}
        {cart.length > 0 && (
          <Paper
            elevation={8}
            sx={{ 
              p: isAnyLandscape ? 0.75 : 2, 
              borderTop: '2px solid #b5cbb3',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: isAnyLandscape ? 48 : 'auto'
            }}
          >
            <Box>
              <Typography variant={isAnyLandscape ? "caption" : "body2"} color="text.secondary" sx={{ lineHeight: isAnyLandscape ? 1.2 : 1.5 }}>
                {cartItemsCount} {t('menu.cart').toLowerCase()}
              </Typography>
              <Typography variant={isAnyLandscape ? "body2" : "h6"} sx={{ fontWeight: 600, lineHeight: isAnyLandscape ? 1.2 : 1.5 }}>
                {t('common.total')}: €{cartTotal.toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size={isAnyLandscape ? "small" : "large"}
              onClick={handleConfirmOrder}
              sx={{
                backgroundColor: '#b5cbb3',
                color: '#1a1a1a',
                py: isAnyLandscape ? 0.5 : 1,
                '&:hover': {
                  backgroundColor: '#a3b9a1'
                }
              }}
            >
              {t('menu.sendOrder')}
            </Button>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MenuDialog;